import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import React from 'react';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import { DialogContent } from 'Views/Page/Content/Modal/Components';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import AssetAndLiabilityDialogActions
  from 'Views/Page/Content/Templates/DigitalOnboardingRealAssetInfo/AssetAndLiabilityDialogActions';
import fr from 'facet-rest';
import { FDBodyText, FDBox, FDStack } from '@facet-wealth/design';
import PropTypes from 'prop-types';


const BalanceSheetAccountTypeTemplate = ({ onRefresh, infoLayoutComponent, useFD }) => {
  const { resource, metaOverrideSet, saving } = useMetaLayoutContext();
  const [infoResource, setInfoResource] = React.useState(null);
  const { displayErrorMessage } = useSnackbarContext();
  const { clearModalCollection, setModalCollection } = useModalContext();
  const [loading, setLoading] = React.useState(resource.attr('accountSubType') !== '');
  const [initializing, setInitializing] = React.useState(true);

  const handleSaveAndClose = async () => {
    try {
      setLoading(true);
      await infoResource.submit();
      await onRefresh();
      clearModalCollection();
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Error saving');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndAddAnother = async (rel) => {
    try {
      setLoading(true);
      await infoResource.submit();
      const modalColl = await rel.link('related').get();
      setModalCollection(modalColl);
      setInfoResource(null);
      onRefresh(); // Not awaiting because the user should be able to interact with the new form before we finish refreshing - we can do this refresh in the background
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Error saving and adding another');
    } finally {
      setLoading(false);
    }
  };

  const onAccountSubTypeChange = async () => {
    try {
      let infoResourceCopy;
      if (infoResource) {
        infoResourceCopy = infoResource.copy();
      }

      const [res] = await resource.submit();
      if (infoResourceCopy) {
        res.attr('name', infoResourceCopy.attr('name'));
        res.attr('searchableFinancialInstitutionID', infoResourceCopy.attr('searchableFinancialInstitutionID'));
        res.attr('balance', infoResourceCopy.attr('balance'));
        res.attr('ownerId', infoResourceCopy.attr('ownerId'));
        res.attr('ownerNotes', infoResourceCopy.attr('ownerNotes'));
      }
      setInfoResource(res);
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to load for account type');
    } finally {
      setLoading(false);
    }
  };

  useComponentDidMount(async () => {
    if (resource.attr('accountSubType') !== '') {
      await onAccountSubTypeChange(resource.attr('accountSubType'));
    }
    setInitializing(false);
  });

  if (initializing) {
    return (
      <LoadingIndicator align={'center'} />
    );
  }

  return (
    <>
      <DialogContent>
        <FDStack direction={'column'} spacing={4} height={'100%'} justifyContent={'space-between'}>
          <FDStack direction={'column'} spacing={infoResource ? 0 : 5}>
            <FDStack direction={'column'} spacing={2}>
              <MetaControlField attr={'title'} />
              <FDBox paddingX={3} width={'100%'} display={'flex'}>
                <MetaControlField attr="accountSubType" onInlineSubmit={(val) => onAccountSubTypeChange(val)} />
              </FDBox>
            </FDStack>
            { infoResource
              ? (<MetaResourceRenderer
                resource={infoResource}
                layout={infoLayoutComponent}
                LayoutProps={{
                  isPartOfTypeStep: true,
                }}
                metaOverrideSet={metaOverrideSet}
              />) : (
                <FDBox paddingX={3}>
                  <FDBodyText color={'textDisabled'} value={'Please select a type first.'} />
                </FDBox>
              ) }
          </FDStack>
          <AssetAndLiabilityDialogActions
            addAnotherRel={resource.relationship(fr.relationships.addManualAccount.name)}
            handleSaveAndAddAnother={handleSaveAndAddAnother}
            handleSaveAndClose={handleSaveAndClose}
            isLoading={loading || saving}
            isDisabled={!infoResource}
            useFD={useFD}
          />
        </FDStack>
      </DialogContent>
    </>
  );
};

BalanceSheetAccountTypeTemplate.defaultProps = {

};

BalanceSheetAccountTypeTemplate.propTypes = {
  onRefresh: PropTypes.func.isRequired,
  infoLayoutComponent: PropTypes.object.isRequired,
  useFD: PropTypes.bool.isRequired,
};

export default BalanceSheetAccountTypeTemplate;
